// code adapted from here: https://github.com/visgl/deck.gl/blob/8.1-release/modules/core/bundle/deckgl.js
// but removing the dependency on the 'react-map-gl' component

import { Deck, type DeckProps } from '@deck.gl/core'
import maplibregl from 'maplibre-gl'
import { Basemap } from '@workspaces/types'

export const ZOOM_TRANSITION_DURATION = 250

interface MapViewState {
  longitude: number
  latitude: number
  zoom: number
  bearing?: number
  pitch?: number
}

interface DeckMapProps extends Record<string, unknown> {
  container?: string | HTMLElement
  basemap?: Basemap.Basemap | null
  mapStyle?: string
  viewState?: MapViewState
  initialViewState?: MapViewState
}

interface BoundingBox {
  center: [number, number]
  zoom: number
  provider: Basemap.Basemap
}

interface MapRef {
  onUpdateViewState(boundingBox: BoundingBox): void
}

// maplibre-gl private members we rely on (not part of its public typings).
// MapLibre forked from mapbox-gl v1, so these internals carry over; the call
// site guards against them being absent in case a future version drops them.
interface MapLibreInternals {
  style: unknown
  _frame: { cancel(): void } | null
  _render?: () => void
}

const CANVAS_STYLE = {
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
}

const DEFAULT_MAP_PROPS = {
  layers: [],
  basemap: null,
  controller: { scrollZoom: { smooth: true, speed: ZOOM_TRANSITION_DURATION } },
  useDevicePixels: 2,
  getCursor: ({ isDragging }: { isDragging: boolean }) =>
    isDragging ? 'grabbing' : 'pointer',
  getTooltip({
    x,
    y,
    object,
    layer,
  }: {
    x: number
    y: number
    object: unknown
    layer: { props: Record<string, unknown> }
  }) {
    if (!object) {
      return
    }
    const tooltipFn = layer.props.getTooltip
    if (typeof tooltipFn === 'function') {
      return tooltipFn({ object, layer, x, y })
    }
  },
  layerFilter({
    layer,
    viewport,
  }: {
    layer: { props: Record<string, unknown> }
    viewport: unknown
  }) {
    const filterFn = layer.props.layerFilter
    if (typeof filterFn === 'function') {
      return filterFn(viewport, layer)
    }
    return true
  },
}

// Create canvas elements for mapbox and deck
function createCanvas(props: DeckMapProps) {
  const container: HTMLElement | null =
    typeof props.container === 'string'
      ? document.querySelector(props.container)
      : props.container ?? document.body

  if (!container) {
    throw new Error('[DeckMap] container not found')
  }

  const containerStyle = window.getComputedStyle(container)
  if (containerStyle.position === 'static') {
    container.style.position = 'relative'
  }

  const mapboxCanvas = document.createElement('div')
  container.appendChild(mapboxCanvas)
  Object.assign(mapboxCanvas.style, CANVAS_STYLE)

  const deckCanvas = document.createElement('canvas')
  deckCanvas.oncontextmenu = () => false // this disables right click on map
  container.appendChild(deckCanvas)
  Object.assign(deckCanvas.style, CANVAS_STYLE)

  return { container, mapboxCanvas, deckCanvas }
}

/**
 * @params container (Element) - DOM element to add deck.gl canvas to
 * @params map (Object) - map API. Set to falsy to disable
 */
export default class DeckMap extends Deck {
  appBasemap: Basemap.Basemap = Basemap.Basemap.OpenStreetMaps
  currentBoundingBox: BoundingBox | null = null
  mapRef: MapRef | null = null
  private _map?: maplibregl.Map
  private _onBeforeRender?: (params: unknown) => void

  constructor(props: DeckMapProps = {}) {
    props = {
      ...DEFAULT_MAP_PROPS,
      ...props,
    }

    props.onResize = () => {
      if (this._map) {
        this._map.resize()
      }
    }

    const { mapboxCanvas, deckCanvas } = createCanvas(props)

    super({ canvas: deckCanvas, ...props } as DeckProps)

    const viewState = props.viewState || props.initialViewState
    this.appBasemap = props.basemap ?? Basemap.Basemap.OpenStreetMaps

    this._map = new maplibregl.Map({
      container: mapboxCanvas,
      style: props.mapStyle,
      interactive: false,
      center: [viewState?.longitude ?? 0, viewState?.latitude ?? 0],
      zoom: viewState?.zoom,
      bearing: viewState?.bearing || 0,
      pitch: viewState?.pitch || 0,
    })

    // Update base map. deck.gl 9 invokes onBeforeRender with { device, gl };
    // we keep the param loose since we only forward it through.
    this._onBeforeRender = (params: unknown) => {
      this.onBeforeRender(params)
      if (this._map) {
        // getViewports() is transiently empty on some frames (e.g. a redraw
        // forced before deck has recomputed the viewport — see
        // scheduleForcedRedraw). Reading viewport.longitude when undefined
        // throws inside onBeforeRender, which aborts the whole frame: the
        // basemap sync fails AND deck's own layer reconcile/draw for that frame
        // is lost. Guard it. (deck.gl 9 migration, sc-552667)
        const viewport = this.getViewports()[0] as unknown as
          | MapViewState
          | undefined
        if (viewport) {
          this._map.jumpTo({
            center: [viewport.longitude, viewport.latitude],
            zoom: viewport.zoom,
            bearing: viewport.bearing,
            pitch: viewport.pitch,
          })
          // TODO: only redraw when viewport has changed
          this.redrawMapbox()
        }
      }
    }
    // Install the basemap-sync hook as deck's onBeforeRender. deck.gl 9 no
    // longer re-applies onBeforeRender through the setProps() swap the way v8
    // did (the app never passes onBeforeRender, so the swap condition was never
    // met and the hook stayed uninstalled → the MapLibre basemap never followed
    // deck's view). Setting it explicitly makes deck invoke it every frame.
    this.setProps({ onBeforeRender: this._onBeforeRender } as DeckProps)
  }

  // deck.gl 9's viewState-transition clock is the luma Timeline, which only
  // advances on rendered frames. deck skips frames while nothing needs
  // redrawing, so after an idle period the first rendered frame applies the
  // whole idle gap as elapsed time at once — a transition started while idle is
  // fast-forwarded by that gap (or finishes instantly), which reads as the
  // camera snapping instead of flying. Clearing lastEngineTime makes the next
  // frame contribute a zero delta, so a transition started now runs its full
  // duration. Call this BEFORE setting a transition viewState.
  resetTransitionClock() {
    const timeline = (
      this as unknown as {
        animationLoop?: { timeline?: { lastEngineTime: number } }
      }
    ).animationLoop?.timeline
    if (timeline) {
      timeline.lastEngineTime = -1
    }
  }

  // deck.gl 9's AnimationLoop does not render on its own here: setProps() and
  // Deck.redraw() only flag `_needsRedraw`, but nothing drives a frame while the
  // map is idle (no camera transition, no pointer interaction). So a layer
  // update issued while idle — e.g. excluding assets, which only changes layer
  // data, not the camera — is staged in `deck.props.layers` but never
  // reconciled or drawn, leaving the previous icons on screen. Calling the
  // AnimationLoop's own `redraw()` forces the pending frame (reconcile + draw).
  //
  // Only call on an idle update (no active camera transition): a forced frame
  // advances the transition clock (see resetTransitionClock), so forcing during
  // a flyTo fast-forwards/cancels it. Deferred + coalesced onto the next
  // animation frame — invoking redraw() synchronously re-enters the render
  // cycle and crashes the renderer. (deck.gl 9 migration, sc-552667)
  scheduleForcedRedraw() {
    const self = this as unknown as {
      _forcedRedrawScheduled?: boolean
      animationLoop?: { redraw?: () => void }
    }
    if (self._forcedRedrawScheduled) {
      return
    }
    self._forcedRedrawScheduled = true
    requestAnimationFrame(() => {
      self._forcedRedrawScheduled = false
      self.animationLoop?.redraw?.()
    })
  }

  // Custom render hook the parent stores in its props; declared here so we can
  // both call it and swap it in setProps (see below).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBeforeRender(params: unknown): void {
    // Default no-op; replaced with the caller's injected callback in setProps().
  }

  setMapRef(mapRef: MapRef) {
    this.mapRef = mapRef
  }

  getAppBasemap() {
    return this.appBasemap
  }

  updateCurrentBoundingBox(center: [number, number], zoom: number) {
    const provider = this.appBasemap
    this.currentBoundingBox = {
      center,
      zoom,
      provider,
    }
    this.mapRef?.onUpdateViewState(this.currentBoundingBox)
  }

  getCurrentBoundingBox() {
    return this.currentBoundingBox
  }

  // code taken from here: https://github.com/visgl/react-map-gl/blob/ce6f6662ca34f8765cf0f515039e316adb52a957/src/mapbox/mapbox.js#L421
  // Force redraw the map now. Typically resize() and jumpTo() is reflected in the next
  // render cycle, which is managed by Mapbox's animation loop.
  // This removes the synchronization issue caused by requestAnimationFrame.
  redrawMapbox() {
    const map = this._map
    if (!map) {
      return
    }
    const mapInternals = map as unknown as MapLibreInternals
    // _render throws if style does not exist; only force a synchronous redraw
    // when both the style and the private _render hook are available.
    if (mapInternals.style && typeof mapInternals._render === 'function') {
      // cancel the scheduled update
      if (mapInternals._frame) {
        mapInternals._frame.cancel()
        mapInternals._frame = null
      }
      // the order is important - render() may schedule another update
      mapInternals._render()
    }
  }

  finalize() {
    if (this._map) {
      console.debug('🌎 DECK: Finalizing DeckMap instance')
      this._map.remove()
    }
    super.finalize()
  }

  setProps(props: DeckProps) {
    // Replace user callback with our own
    // `setProps` is first called in parent class constructor
    // During which this._onBeforeRender is not defined
    // It is called a second time in _onRendererInitialized with all current props
    if (
      'onBeforeRender' in props &&
      this._onBeforeRender &&
      props.onBeforeRender !== this._onBeforeRender
    ) {
      this.onBeforeRender = props.onBeforeRender as (params: unknown) => void
      props.onBeforeRender = this._onBeforeRender
    }

    super.setProps(props)
  }
}
